// @flow

import { useState, useEffect, useMemo } from 'react';

import { useDispatch } from 'react-redux';

import { selectProductsByType } from '@gandi/cart';

import { notifyDanger } from '@gandi/notifications';

import { useTranslator } from '@gandi/translate';

import { SrOnly } from '@gandi/ui';

import { useAppSelector } from 'src/hooks';

import type { ProductSummary } from 'src/modules/mailbox-standalone/resources/products';

import { useProductPrice } from '../../../modules/hooks/useProductPrice';
import { useUpsertCartMailboxes } from '../../../modules/hooks/useUpsertCartMailboxes';
import Price from '../../Price';

import styles from './BillingChoice.scss';

export function BillingChoice({
  productType,
  product,
}: {
  product: ProductSummary,
  productType: { productType: string },
}): React.MixedElement {
  const { __ } = useTranslator();

  const cartProducts = useAppSelector(state =>
    selectProductsByType(state, productType),
  );

  const { handleUpsertCartMailboxes } = useUpsertCartMailboxes();

  const dispatch = useDispatch();

  const cartProductItem = cartProducts?.find(
    item => item.payload.config_name === product.config_name,
  );

  const selectedCartDuration = cartProductItem?.payload?.duration_month;

  const [billingCycle, setBillingCycle] = useState(selectedCartDuration);

  const displayPrices = useMemo(() => {
    if (!product?.prices) return [] as ProductSummary['prices'];

    const monthlyPrice = product.prices.find(p => p.min_duration === 1);
    const yearlyPrice = product.prices.find(p => p.min_duration === 12);

    return [monthlyPrice, yearlyPrice].filter(Boolean);
  }, [product?.prices]);

  async function handleChange(e: any) {
    const value = Number(e.target.value);
    setBillingCycle(value);

    try {
      await handleUpsertCartMailboxes(
        {
          ...cartProductItem?.payload,
          duration_month: value,
        },
        cartProductItem?.id,
      );
    } catch (err) {
      dispatch(
        notifyDanger({
          message:
            err?.response?.data?.message ||
            __('We are unable to add mailboxes to your cart.'),
        }),
      );
    }
  }

  useEffect(() => {
    if (
      Number.isFinite(selectedCartDuration) &&
      selectedCartDuration !== billingCycle
    ) {
      setBillingCycle(selectedCartDuration);
    }
  }, [billingCycle, selectedCartDuration]);

  return (
    <>
      {cartProductItem && (
        <div className={styles.billingContainer}>
          <p className={styles.billingTitle}>{__('Billing cycle')}</p>
          <div className={styles.billingRadioContainer}>
            {displayPrices.map(cycle => {
              const id = `billing-${product.config_name}-${cycle.duration_unit}-${cycle.min_duration}`;
              const name = `billingCycle-${product.config_name}`;

              return (
                <BillingChoiceOption
                  key={id}
                  id={id}
                  name={name}
                  checked={cycle.min_duration === billingCycle}
                  product={product}
                  cycle={cycle}
                  handleChange={handleChange}
                />
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function BillingChoiceOption({
  cycle,
  id,
  name,
  checked,
  product,
  handleChange,
}: {
  cycle: ProductSummary['prices'][0],
  id: string,
  name: string,
  checked: boolean,
  product: ProductSummary,
  handleChange: (e: mixed) => Promise<mixed>,
}): React.MixedElement {
  const { __ } = useTranslator();

  const priceProps = useProductPrice({
    product,
    duration: cycle.min_duration,
    minDuration: cycle.min_duration,
  });

  return (
    // eslint-disable-next-line @gandi/gandi/no-global-element
    <label key={id} htmlFor={id} className={styles.billingInput}>
      <SrOnly>
        {/* eslint-disable-next-line @gandi/gandi/no-global-element */}
        <input
          type="radio"
          id={id}
          name={name}
          value={cycle.min_duration}
          checked={checked}
          onChange={handleChange}
        />
      </SrOnly>
      <div className={styles.billingContent}>
        <p className={styles.billingTitleContent}>
          {/**
           * Don't rely solely on duration_unit: it is wrong on this API:
           * a catalog with `min_duration: 12` should not have `duration_unit: 'year'`:
           * it would mean 12 years, not 12 months.
           */}
          {cycle.min_duration === 1 &&
          /* $FlowFixMe[incompatible-type] */
          cycle.duration_unit === 'month'
            ? __('Monthly')
            : __('Yearly')}
        </p>
        <div className={styles.amountContainer}>
          <Price {...priceProps} subtitle={__('per mailbox')} />
        </div>
      </div>
    </label>
  );
}
